/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-mixed-operators */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, CSSProperties } from 'react';
import classnames from 'classnames';
import { nanoid } from 'nanoid';

import styles from './index.module.css';

interface CarouselData {
	key: string;
	data: Array<any>;
	position: number;
	id: number;
}

// transfer general data into carousel Data which has key and position
const getInitializeState = (
	data: Array<any>,
	totalWidthPerItem: number,
	isActiveCarousel: boolean,
): CarouselData[] => {
	let copyMappingData = data.slice();
	if (isActiveCarousel) {
		copyMappingData = [
			copyMappingData[copyMappingData.length - 1],
			...copyMappingData,
			copyMappingData[0],
		];
	}
	let carouselData: CarouselData[] = [];
	carouselData = copyMappingData.map((item, i) => ({
		data: item,
		key: nanoid(),
		position: isActiveCarousel ? (i - 1) * totalWidthPerItem : totalWidthPerItem * i,
		id: item.id,
	}));

	return carouselData;
};

type Animation = 'normal' | 'upAndDown';

const getAnimationStyle = (
	animation: Animation,
	position: number,
	index: number,
	id: number,
): CSSProperties => {
	if (animation === 'normal') {
		return {
			transform: `translateX(${position}px)`,
			transition: 'transform 500ms ease',
			top: id % 2 === 0 ? '0' : '80px',
		};
	}
	if (animation === 'upAndDown') {
		return {
			transform:
				index % 2 === 0
					? `translateX(${position}px) `
					: `translateX(${position}px) translateY(80px)`,
			transition: 'transform 500ms ease',
		};
	}
	return {};
};

interface CarouselProperty {
	data: Array<any>;
	widthOfItem: number;
	Component: React.FC<any>;
	heightOfCarousel?: number;
	animation?: 'normal' | 'upAndDown';
	marginBetweenItems?: number;
	automatic?: 'left' | 'right';
	className?: string;
}

const Carousel: React.FC<CarouselProperty> = ({
	data,
	widthOfItem,
	marginBetweenItems = 0,
	Component,
	automatic,
	animation = 'normal',
	heightOfCarousel,
	className,
}) => {
	const totalWidthPerItem = widthOfItem + marginBetweenItems;
	const maxCarouselWidth = window.innerWidth;
	const maxShowItemCount = Math.ceil(maxCarouselWidth / totalWidthPerItem);
	const isActiveCarousel = data.length >= maxShowItemCount;

	const [carouselData, setCarouselData] = useState<CarouselData[]>(() =>
		getInitializeState(data, totalWidthPerItem, isActiveCarousel),
	);

	// 往左邊
	const handleClickNext = () => {
		let newData = carouselData.slice();
		// 每一項往左移 - totalWidthPerItem
		newData = newData.map(item => ({ ...item, position: item.position - totalWidthPerItem }));
		// 篩掉已經在 track 之外的項目
		newData = newData.filter(item => item.position >= -totalWidthPerItem);
		// 補充 track 尾項，根據觀察為第二項，也就是位置為 0 的項目
		const supply = newData.find(item => item.position === 0) as CarouselData;
		newData = [...newData, { ...supply, key: nanoid(), position: totalWidthPerItem * data.length }];
		setCarouselData(newData);
	};

	// 往右邊
	const handleClickLast = () => {
		let newData = carouselData.slice();
		// 每一項往右移 + totalWidthPerItem
		newData = newData.map(item => ({ ...item, position: item.position + totalWidthPerItem }));
		// 篩掉已經在 track 之外的項目，允許的最大 track position 為 totalWidthPerItem * (newData.length - 2)
		newData = newData.filter(item => item.position <= totalWidthPerItem * (newData.length - 2));
		// 補充 track 首項，根據觀察為 track 倒數第二項
		const supply = newData.find(
			item => item.position === totalWidthPerItem * (newData.length - 2),
		) as CarouselData;
		newData = [...newData, { ...supply, key: nanoid(), position: -totalWidthPerItem }];
		setCarouselData(newData);
	};

	useEffect(() => {
		if (!isActiveCarousel) return;
		let timeoutID: number;
		if (automatic === 'left') {
			timeoutID = window.setTimeout(handleClickNext, 5000);
		}
		if (automatic === 'right') {
			timeoutID = window.setTimeout(handleClickLast, 5000);
		}
		// eslint-disable-next-line consistent-return
		return () => {
			if (timeoutID) {
				window.clearTimeout(timeoutID);
			}
		};
	}, [automatic, carouselData]);

	return (
		<div
			className={classnames(styles.carousel, className)}
			style={{ height: `${heightOfCarousel}px` }}
		>
			{isActiveCarousel && !automatic && (
				<div className={styles.buttons}>
					<button type="button" onClick={handleClickLast}>
						last
					</button>
					<button type="button" onClick={handleClickNext}>
						next
					</button>
				</div>
			)}
			<div className={styles.track}>
				{carouselData.map((item, i) => (
					<div style={getAnimationStyle(animation, item.position, i, item.id)} key={item.key}>
						<Component {...item.data} />
					</div>
				))}
			</div>
		</div>
	);
};

export default Carousel;
